"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  Root,
  Title,
  Close,
  Content,
  Overlay,
  Portal,
} from "@radix-ui/react-dialog";
import { X, InfoIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { PortableTextBlock } from "next-sanity";
import RichText from "./rich-text";

type PageHeaderProps = {
  title: string;
  alt: string;
  src: string;
  titleColor?: "text-primary" | "text-white";
  className?: string;
  introduction?: string;
  dialogContent?: PortableTextBlock[];
};

export default function PageHeader({
  title,
  alt,
  src,
  className,
  titleColor = "text-white",
  introduction,
  dialogContent,
}: PageHeaderProps) {
  const [showDialog, setShowDialog] = useState(false);
  const t = useTranslations();

  useEffect(() => {
    if (dialogContent?.length) {
      setShowDialog(true);
    }
  }, []);

  return (
    <section
      className={twMerge(
        "flex flex-col-reverse gap-y-12 items-center md:flex-row md:justify-between",
        className,
      )}
    >
      <div className="flex flex-col gap-y-4">
        <h1 className={titleColor}>{title}</h1>
        {introduction && (
          <span className="text-primary text-xl font-bold">{introduction}</span>
        )}
      </div>
      <Image
        src={src}
        alt={alt}
        width={1000}
        height={1000}
        priority
        className="w-40 md:w-80 rotate-12 mr-8"
      />
      <Root open={showDialog} onOpenChange={() => setShowDialog(!showDialog)}>
        <Portal>
          <Overlay className=" bg-accent/10 backdrop-blur-2xl z-20 fixed inset-0" />
          <Content className="bg-primary z-50 text-neutral lg:rounded-lg shadow-2xl fixed top-0 left-0 w-screen h-screen lg:top-1/2 lg:left-1/2 lg:-translate-x-[50%] lg:-translate-y-[50%] lg:w-[1000px] lg:h-2/3 overflow-y-scroll p-8 focus-visible:outline-none flex flex-col justify-between gap-4">
            <div className="flex items-center justify-between">
              <Title>Info</Title>
              <InfoIcon className="h-16 w-16 text-accent" />
            </div>
            <RichText text={dialogContent} />
            <Close className="button text-white! !hover:text-accent! focus-visible:outline-none self-end">
              {t("page.close")} <X className="h-5 w-5" />
            </Close>
          </Content>
        </Portal>
      </Root>
    </section>
  );
}
